// ----------------------------------------------------------------------------
// Component: Accordion
// ----------------------------------------------------------------------------
//
// Generic accordion component.
//
// Requires the following structure:
// .js-accordion__item
//     .js-accordion__button
//     .js-accordion__content
//
// When the button is clicked, 'isOpen' class is added/removed for all three
// elements.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Styles
import './accordion.scss';

// -------------------------------------
// Variables
// -------------------------------------

var ACCORDION = '.js-accordion';
var ACCORDION_BUTTON = '.js-accordion__button';
var ACCORDION_ITEM = '.js-accordion__item';
var ACCORDION_CONTENT = '.js-accordion__content';
var ACCORDION_TITLE = '.accordion__title';
var ACCORDION_CONTENT_MOBILE = '.js-accordion__content--mobile-only';

// Constant trigger classes
var OPEN_CLASS = 'is-open';

// -------------------------------------
// Helper Functions
// -------------------------------------

function setTabIndex(e, value) {
  e.setAttribute('tabindex', value);
}
function toggleAccordion(accordion) {
  document.querySelectorAll(ACCORDION_ITEM).forEach(item => {
    item.classList.remove(OPEN_CLASS);
  });
  document.querySelectorAll(ACCORDION_CONTENT).forEach(item => {
    item.classList.remove(OPEN_CLASS);
    item.style.height = 0;
    item.hidden = true;
  });
  document.querySelectorAll(ACCORDION_BUTTON).forEach(item => {
    item.classList.remove(OPEN_CLASS);
    item.setAttribute('aria-expanded', 'false');
  });
  accordion.item.parentElement.querySelectorAll('.' + OPEN_CLASS).forEach(function (e) {
    e.classList.remove(OPEN_CLASS);
  });
  accordion.item.parentElement.querySelectorAll(ACCORDION_CONTENT).forEach(function (e) {
    e.hidden = true;
    e.style.height = null;
  });
  accordion.item.parentElement.querySelectorAll(ACCORDION_BUTTON).forEach(function (e) {
    e.setAttribute('aria-expanded', 'false');
  });
  accordion.item.parentElement.querySelectorAll(ACCORDION_CONTENT).forEach(function (e) {
    e.querySelectorAll('a').forEach(el => {
      setTabIndex(el, -1);
    });
  });
  if (accordion.isOpening) {
    accordion.button.classList.add(OPEN_CLASS);
    accordion.item.classList.add(OPEN_CLASS);
    accordion.content.classList.add(OPEN_CLASS);
    accordion.content.style.height = accordion.content.scrollHeight + 'px';
    accordion.button.setAttribute('aria-expanded', 'true');
    accordion.content.hidden = false;
    accordion.content.querySelectorAll('a').forEach(function (el) {
      setTabIndex(el, 0);
    });
  }
}
function accordionEventHandle(e, button) {
  var accordion = {
    button: button,
    item: button.closest(ACCORDION_ITEM),
    title: button.closest(ACCORDION_TITLE),
    content: button.closest(ACCORDION_TITLE).nextElementSibling,
    isOpening: !button.classList.contains(OPEN_CLASS),
    container: button.closest(ACCORDION),
    button_group: button.closest(ACCORDION).querySelectorAll(ACCORDION_BUTTON)
  };
  if (e.type === 'click') {
    e.preventDefault();
    var isOpening = !button.classList.contains(OPEN_CLASS);

    // Before toggling, find the open panel that's about to close.
    // If it sits above the clicked button and has a CSS height transition,
    // its shrinking height progressively shifts the viewport toward the footer.
    // A ResizeObserver tracks each frame of the animation and scrolls up by
    // the same amount, keeping the clicked heading in place.
    var ro = null;
    if (isOpening && 'ResizeObserver' in window) {
      var openContent = accordion.item.parentElement.querySelector("".concat(ACCORDION_CONTENT, ".").concat(OPEN_CLASS));
      if (openContent) {
        var openHeight = openContent.getBoundingClientRect().height;
        var openTop = openContent.getBoundingClientRect().top;
        var btnTop = button.getBoundingClientRect().top;
        if (openHeight > 0 && openTop < btnTop) {
          var prevHeight = openHeight;
          ro = new ResizeObserver(() => {
            var h = openContent.getBoundingClientRect().height;
            var shrinkage = prevHeight - h;
            prevHeight = h;
            if (shrinkage > 0.5) window.scrollBy(0, -shrinkage);
            if (h <= 0.5) ro.disconnect();
          });
          ro.observe(openContent);
        }
      }
    }
    toggleAccordion(accordion);

    // Fallback: no collapsing panel above, just scroll the heading into view.
    if (isOpening && !ro) {
      accordion.item.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  } else if (e.type === 'keydown') {
    var key = e.which.toString();
    if (key.match(/35|36|38|40/)) {
      e.preventDefault();
      var index = Array.prototype.indexOf.call(accordion.button_group, button);
      var length = accordion.button_group.length;
      var newIndex;
      switch (key) {
        case '38':
          // Up
          newIndex = (index + length + -1) % length;
          accordion.button_group[newIndex].focus();
          break;
        case '40':
          // Down
          newIndex = (index + length + 1) % length;
          accordion.button_group[newIndex].focus();
          break;
        case '35':
          // End
          accordion.button_group[length - 1].focus();
          break;
        case '36':
          // Home
          accordion.button_group[0].focus();
          break;
      }
    }
  }
}
function initAccordionTabIndex() {
  var accordionContentLinks;
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 1024) {
    accordionContentLinks = document.querySelectorAll(ACCORDION_CONTENT + ' a');
  } else {
    accordionContentLinks = document.querySelectorAll("".concat(ACCORDION_CONTENT, ":not(").concat(ACCORDION_CONTENT_MOBILE, ") a"));
  }
  accordionContentLinks.forEach(function (el) {
    setTabIndex(el, -1);
  });
}

// -------------------------------------
// Hooks (event delegation so accordions work after section re-render)
// -------------------------------------

document.addEventListener('click', function (e) {
  var button = e.target.closest(ACCORDION_BUTTON);
  if (!button) return;
  accordionEventHandle(e, button);
});
document.addEventListener('keydown', function (e) {
  var button = e.target.closest(ACCORDION_BUTTON);
  if (!button) return;
  accordionEventHandle(e, button);
});
document.addEventListener('DOMContentLoaded', initAccordionTabIndex);
document.addEventListener('shopify:section:load', initAccordionTabIndex);
window.addEventListener('resize', function () {
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 1024) {
    // On mobile, find all accordion content links of the close accordions to add tabindex -1
    var accordionContentLinks = document.querySelectorAll("".concat(ACCORDION_CONTENT, ":not(.").concat(OPEN_CLASS, ") a"));
    accordionContentLinks.forEach(function (e) {
      setTabIndex(e, -1);
    });
  } else {
    // If on desktop, find all accordion content links that belongs to the #accordion-cart--content on the cart page to remove tabindex -1.
    var accordionCartContentLinks = document.querySelectorAll("".concat(ACCORDION_CONTENT).concat(ACCORDION_CONTENT_MOBILE, " a"));
    accordionCartContentLinks.forEach(function (e) {
      setTabIndex(e, 0);
    });
  }

  // Re-adjust all the expanded accordion content tab height on "resize" event by setting their height to "auto"
  var expandedAccordionContent = document.querySelectorAll("".concat(ACCORDION_CONTENT, ".").concat(OPEN_CLASS));
  expandedAccordionContent.forEach(function (accordionContent) {
    accordionContent.style.height = 'auto';
  });
});